/**
 * TOSS Web Application - Messenger API Service
 *
 * API functions for messenger endpoints:
 * - Chat management (CRUD, members)
 * - Message management (send, edit, delete, read)
 * - Search and online users
 */

import apiClient from './client';
import type {
  ChatShort,
  ChatFull,
  ChatMember,
  ChatMessage,
  ChatListResponse,
  MessageListResponse,
  MessageContextResponse,
  SearchResultResponse,
  UnreadCountResponse,
  PinnedMessage,
  MessageReaction,
  CreateChatRequest,
  CreateCustomerChatRequest,
  CustomerChatCandidate,
  UpdateChatRequest,
  SendMessageRequest,
  EditMessageRequest,
  ForwardMessagesRequest,
  ChatListFilter,
  MessageListFilter,
} from '@/types/messenger.types';
import type { ChatDeleteScope } from '@/utils/chatDeleteDialog';
import { ChatMemberRole } from '@/types/messenger.types';

// ============================================================================
// Endpoints
// ============================================================================

const BASE = '/api/lk/messenger';

const ENDPOINTS = {
  CHATS: `${BASE}/chats`,
  CHAT: (id: string) => `${BASE}/chats/${id}`,
  CHAT_AVATAR: (id: string) => `${BASE}/chats/${id}/avatar`,
  CHAT_BACKGROUND: (id: string) => `${BASE}/chats/${id}/background`,
  PRIVATE_CHAT: (userId: string) => `${BASE}/chats/find-private/${userId}`,
  MESSAGES: (chatId: string) => `${BASE}/chats/${chatId}/messages`,
  MESSAGE: (chatId: string, messageId: string) => `${BASE}/chats/${chatId}/messages/${messageId}`,
  MARK_READ: (chatId: string, messageId: string) => `${BASE}/chats/${chatId}/messages/${messageId}/read`,
  MARK_ALL_READ: (chatId: string) => `${BASE}/chats/${chatId}/read-all`,
  ADD_MEMBER: (chatId: string) => `${BASE}/chats/${chatId}/members`,
  REMOVE_MEMBER: (chatId: string, userId: string) => `${BASE}/chats/${chatId}/members/${userId}`,
  UPDATE_MEMBER_ROLE: (chatId: string, userId: string) => `${BASE}/chats/${chatId}/members/${userId}/role`,
  LEAVE_CHAT: (chatId: string) => `${BASE}/chats/${chatId}/leave`,
  PIN_CHAT: (chatId: string) => `${BASE}/chats/${chatId}/pin`,
  PIN_MESSAGE: (chatId: string) => `${BASE}/chats/${chatId}/pin-message`,
  MESSAGE_CONTEXT: (chatId: string, messageId: string) => `${BASE}/chats/${chatId}/messages/${messageId}/context`,
  REACTIONS: (chatId: string, messageId: string) => `${BASE}/chats/${chatId}/messages/${messageId}/reactions`,
  REACTION: (chatId: string, messageId: string, emoji: string) =>
    `${BASE}/chats/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
  FILE_MESSAGES: (chatId: string) => `${BASE}/chats/${chatId}/files`,
  FORWARD: (chatId: string) => `${BASE}/chats/${chatId}/messages/forward`,
  POST_APPLICATION_CARD: (chatId: string) => `${BASE}/chats/${chatId}/cards/application`,
  CUSTOMER_CHATS: `${BASE}/customer-chats`,
  CUSTOMER_CHAT: (id: string) => `${BASE}/customer-chats/${id}`,
  CUSTOMER_CHAT_CANDIDATES: (customerId: string) => `${BASE}/customer-chats/candidates/${customerId}`,
  SEARCH: `${BASE}/search`,
  ONLINE_USERS: `${BASE}/online`,
  UNREAD_COUNT: `${BASE}/unread-count`,
} as const;

// ============================================================================
// Chat API Functions
// ============================================================================

export async function getChats(params?: ChatListFilter): Promise<ChatListResponse> {
  const response = await apiClient.get<ChatListResponse>(ENDPOINTS.CHATS, { params });
  return response.data;
}

export async function getChatById(chatId: string): Promise<ChatFull> {
  const response = await apiClient.get<ChatFull>(ENDPOINTS.CHAT(chatId));
  return response.data;
}

export async function createChat(data: CreateChatRequest): Promise<ChatFull> {
  const response = await apiClient.post<ChatFull>(ENDPOINTS.CHATS, data);
  return response.data;
}

export async function updateChat(chatId: string, data: UpdateChatRequest): Promise<ChatFull> {
  const response = await apiClient.put<ChatFull>(ENDPOINTS.CHAT(chatId), data);
  return response.data;
}

export async function uploadChatAvatar(chatId: string, file: File): Promise<{ success: boolean; avatarUrl: string; message: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<{ success: boolean; avatarUrl: string; message: string }>(
    ENDPOINTS.CHAT_AVATAR(chatId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function uploadChatBackground(chatId: string, file: File): Promise<{ success: boolean; avatarUrl: string; message: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<{ success: boolean; avatarUrl: string; message: string }>(
    ENDPOINTS.CHAT_BACKGROUND(chatId),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data;
}

export async function deleteChatBackground(chatId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.CHAT_BACKGROUND(chatId));
}

/**
 * Delete the chat for the caller alone (`scope=me`) or for every participant, permanently
 * (`scope=everyone`) — the server always refuses both on the company's chat with ТОСС
 * (`CHAT_NOT_DELETABLE`) and refuses an omitted scope on a private chat (`CHAT_DELETE_SCOPE_REQUIRED`).
 */
export async function deleteChat(chatId: string, scope: ChatDeleteScope): Promise<void> {
  await apiClient.delete(ENDPOINTS.CHAT(chatId), { params: { scope } });
}

export async function findOrCreatePrivateChat(userId: string): Promise<ChatFull> {
  const response = await apiClient.get<ChatFull>(ENDPOINTS.PRIVATE_CHAT(userId));
  return response.data;
}

// ============================================================================
// Message API Functions
// ============================================================================

export async function getMessages(chatId: string, params?: MessageListFilter): Promise<MessageListResponse> {
  const response = await apiClient.get<MessageListResponse>(ENDPOINTS.MESSAGES(chatId), { params });
  return response.data;
}

export async function sendMessage(chatId: string, data: SendMessageRequest): Promise<ChatMessage> {
  const response = await apiClient.post<ChatMessage>(ENDPOINTS.MESSAGES(chatId), data);
  return response.data;
}

export async function editMessage(chatId: string, messageId: string, data: EditMessageRequest): Promise<ChatMessage> {
  const response = await apiClient.put<ChatMessage>(ENDPOINTS.MESSAGE(chatId, messageId), data);
  return response.data;
}

export async function deleteMessage(chatId: string, messageId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.MESSAGE(chatId, messageId));
}

export async function markAsRead(chatId: string, messageId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.MARK_READ(chatId, messageId));
}

export async function markAllAsRead(chatId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.MARK_ALL_READ(chatId));
}

// ============================================================================
// Member API Functions
// ============================================================================

/**
 * Add somebody to a group or application chat.
 *
 * `grantFullHistory` is consulted for EXTERNAL company chats only, and only changes anything for
 * the mobile audience: a mechanic invited into a customer's conversation reads it from the moment
 * they join, because that conversation is a single window over every request of that customer.
 * Everybody else already gets the whole thing, so the flag is inert for them.
 */
export async function addMember(
  chatId: string,
  userId: string,
  grantFullHistory = false
): Promise<void> {
  await apiClient.post(ENDPOINTS.ADD_MEMBER(chatId), { userId, grantFullHistory });
}

export async function removeMember(chatId: string, userId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.REMOVE_MEMBER(chatId, userId));
}

export async function updateMemberRole(chatId: string, userId: string, role: ChatMemberRole): Promise<ChatMember> {
  const response = await apiClient.put<ChatMember>(ENDPOINTS.UPDATE_MEMBER_ROLE(chatId, userId), { role });
  return response.data;
}

export async function leaveChat(chatId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.LEAVE_CHAT(chatId));
}

// ============================================================================
// Pin API Functions
// ============================================================================

export async function pinChat(chatId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.PIN_CHAT(chatId));
}

export async function unpinChat(chatId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.PIN_CHAT(chatId));
}

// ============================================================================
// Pin Message API Functions
// ============================================================================

export async function pinMessage(chatId: string, messageId: string): Promise<PinnedMessage> {
  const response = await apiClient.post<PinnedMessage>(ENDPOINTS.PIN_MESSAGE(chatId), { messageId });
  return response.data;
}

export async function unpinMessage(chatId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.PIN_MESSAGE(chatId));
}

// ============================================================================
// Reactions
// ============================================================================

/**
 * Ставит эмодзи на сообщение. Отвечает ПОЛНЫМ набором реакций этого сообщения — счётчики и
 * порядок пилюль считает сервер, клиенту остаётся положить ответ в кэш.
 */
export async function addMessageReaction(
  chatId: string,
  messageId: string,
  emoji: string
): Promise<MessageReaction[]> {
  const response = await apiClient.post<MessageReaction[]>(
    ENDPOINTS.REACTIONS(chatId, messageId),
    { emoji }
  );
  return response.data;
}

/** Снимает СВОЮ эмодзи с сообщения. Чужие остаются. */
export async function removeMessageReaction(
  chatId: string,
  messageId: string,
  emoji: string
): Promise<MessageReaction[]> {
  const response = await apiClient.delete<MessageReaction[]>(
    ENDPOINTS.REACTION(chatId, messageId, emoji)
  );
  return response.data;
}

// ============================================================================
// Message Context & File Messages API Functions
// ============================================================================

export async function getMessageContext(chatId: string, messageId: string, surroundingCount?: number): Promise<MessageContextResponse> {
  const response = await apiClient.get<MessageContextResponse>(ENDPOINTS.MESSAGE_CONTEXT(chatId, messageId), {
    params: { surroundingCount },
  });
  return response.data;
}

export async function getFileMessages(chatId: string, params?: MessageListFilter): Promise<MessageListResponse> {
  const response = await apiClient.get<MessageListResponse>(ENDPOINTS.FILE_MESSAGES(chatId), { params });
  return response.data;
}

export async function forwardMessages(targetChatId: string, data: ForwardMessagesRequest): Promise<ChatMessage[]> {
  const response = await apiClient.post<ChatMessage[]>(ENDPOINTS.FORWARD(targetChatId), data);
  return response.data;
}

/**
 * Post an application card into a chat (decision Р27, contract §6).
 *
 * The request carries the application id and nothing else — `content` and `payload`
 * of the resulting MessageType.Card message are minted server-side. Returns 201 with
 * the finished ChatMessage; 404 for a foreign application, 403 for a non-member,
 * 409 CARD_ALREADY_POSTED when this application is already in the chat.
 */
export async function postApplicationCard(chatId: string, applicationId: string): Promise<ChatMessage> {
  const response = await apiClient.post<ChatMessage>(
    ENDPOINTS.POST_APPLICATION_CARD(chatId),
    { applicationId },
  );
  return response.data;
}

// ============================================================================
// Search & Utility API Functions
// ============================================================================

export interface SearchMessagesParams {
  query: string;
  chatId?: string;
  pageSize?: number;
  cursor?: string;
}

export async function searchMessages(
  queryOrParams: string | SearchMessagesParams,
  chatId?: string,
): Promise<SearchResultResponse> {
  // Backwards-compatible signature: supports legacy `searchMessages(query, chatId?)`
  // as well as the paginated `searchMessages({ query, chatId?, pageSize?, cursor? })` form.
  const params: Record<string, string | number> = {};
  if (typeof queryOrParams === 'string') {
    params.query = queryOrParams;
    if (chatId) params.chatId = chatId;
  } else {
    params.query = queryOrParams.query;
    if (queryOrParams.chatId) params.chatId = queryOrParams.chatId;
    if (queryOrParams.pageSize !== undefined) params.pageSize = queryOrParams.pageSize;
    if (queryOrParams.cursor) params.cursor = queryOrParams.cursor;
  }

  const response = await apiClient.get<SearchResultResponse>(ENDPOINTS.SEARCH, { params });
  return response.data;
}

export async function getOnlineUsers(): Promise<string[]> {
  const response = await apiClient.get<string[]>(ENDPOINTS.ONLINE_USERS);
  return response.data;
}

export async function getUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiClient.get<UnreadCountResponse>(ENDPOINTS.UNREAD_COUNT);
  return response.data;
}

/**
 * Заводит дополнительный чат с компанией-заказчиком — смешанную группу из выбранных сотрудников
 * заказчика и выбранных наших.
 *
 * Отдельная функция, а не поле в `createChat`: тот маршрут обслуживает «Новую группу» и всегда
 * рождает чат без компании.
 */
export async function createCustomerChat(data: CreateCustomerChatRequest): Promise<ChatFull> {
  const response = await apiClient.post<ChatFull>(ENDPOINTS.CUSTOMER_CHATS, data);
  return response.data;
}

/** Удаляет дополнительный чат с заказчиком. Сервер откажет, если в нём уже есть переписка. */
export async function deleteCustomerChat(chatId: string): Promise<void> {
  await apiClient.delete(ENDPOINTS.CUSTOMER_CHAT(chatId));
}

/** Портальные учётные записи компании как кандидаты в участники чата. */
export async function getCustomerChatCandidates(customerId: string): Promise<CustomerChatCandidate[]> {
  const response = await apiClient.get<CustomerChatCandidate[]>(
    ENDPOINTS.CUSTOMER_CHAT_CANDIDATES(customerId),
  );
  return response.data;
}

// ============================================================================
// Namespace Export
// ============================================================================

export const messengerApi = {
  getChats,
  getChatById,
  createChat,
  createCustomerChat,
  deleteCustomerChat,
  getCustomerChatCandidates,
  updateChat,
  uploadChatAvatar,
  uploadChatBackground,
  deleteChatBackground,
  deleteChat,
  findOrCreatePrivateChat,
  getMessages,
  sendMessage,
  editMessage,
  deleteMessage,
  markAsRead,
  markAllAsRead,
  addMember,
  removeMember,
  updateMemberRole,
  leaveChat,
  pinChat,
  unpinChat,
  pinMessage,
  unpinMessage,
  addMessageReaction,
  removeMessageReaction,
  getMessageContext,
  getFileMessages,
  forwardMessages,
  postApplicationCard,
  searchMessages,
  getOnlineUsers,
  getUnreadCount,
} as const;

export default messengerApi;
